<script lang="ts">
  import { onDestroy, onMount } from "svelte";
  import { formListStore } from "@entities/form/store.svelte";
  import {
    applyFormSubmissionFieldUpdateProposal,
    approveFormSubmissionFieldUpdateProposalChange,
    clearFormSubmissionMatch,
    confirmFormSubmissionMatch,
    discardFormSubmission,
    formSubmissionDetailStore,
    generateFormSubmissionFieldUpdateProposal,
    markFormSubmissionSpam,
    rejectFormSubmissionFieldUpdateProposalChange,
    rejectFormSubmissionFieldUpdateProposal,
    restoreFormSubmission,
  } from "@entities/form-submission/store.svelte";
  import {
    fieldUpdateProposalActionState,
    parseFieldUpdateProposal,
  } from "@domain/form-submission/field-update-proposal";
  import { sessionStore } from "@features/auth/session/model/store.svelte";
  import { navigate } from "@shared/router/navigation";
  import { canEdit } from "@shared/types/session";
  import type {
    ApplicantType,
    FieldUpdateProposalChange,
    FieldUpdateProposalDecision,
    FieldUpdateProposalStatus,
    FormSubmission,
    FormSubmissionFirmCandidate,
    SubmissionMatchingStatus,
  } from "@shared/types/form-submission";
  import { formatDateTime } from "@shared/util/format";

  interface Props {
    id: string;
  }

  const { id }: Props = $props();

  let actionBusy = $state(false);
  let actionError = $state<string | null>(null);
  let actionMessage = $state<string | null>(null);
  let syncedSubmissionStamp = $state<string | null>(null);
  let matchDraft = $state({
    firm_id: "",
    lawyer_id: "",
  });

  let allowEdit = $derived(
    sessionStore.user ? canEdit(sessionStore.user.role) : false,
  );
  let formOptions = $derived(formListStore.data ?? []);
  let fieldUpdateProposal = $derived(
    parseFieldUpdateProposal(
      formSubmissionDetailStore.data?.field_update_proposal ?? null,
    ),
  );
  let fieldUpdateProposalState = $derived(
    fieldUpdateProposalActionState(fieldUpdateProposal),
  );
  let canGenerateFieldUpdateProposal = $derived(
    canGenerateProposal(formSubmissionDetailStore.data),
  );

  $effect(() => {
    const submission = formSubmissionDetailStore.data;
    if (!submission) return;
    const stamp = `${submission.id}:${submission.updated_at}`;
    if (syncedSubmissionStamp === stamp) return;
    matchDraft = {
      firm_id: submission.matched_firm_id ?? "",
      lawyer_id: submission.matched_lawyer_id ?? "",
    };
    syncedSubmissionStamp = stamp;
  });

  function statusLabel(status: SubmissionMatchingStatus): string {
    if (status === "unmatched") return "未紐付け";
    if (status === "auto_matched") return "自動紐付け";
    if (status === "needs_review") return "要確認";
    if (status === "confirmed") return "確定";
    if (status === "spam") return "スパム";
    return "破棄";
  }

  function statusTagClass(status: SubmissionMatchingStatus): string {
    if (status === "confirmed" || status === "auto_matched")
      return "tag tag-ok";
    if (status === "spam" || status === "discarded") return "tag tag-danger";
    if (status === "needs_review") return "tag tag-warn";
    return "tag";
  }

  function applicantLabel(value: ApplicantType): string {
    if (value === "lawyer") return "弁護士";
    if (value === "staff") return "事務局";
    if (value === "other_professional") return "その他士業";
    return "不明";
  }

  function actionLabel(action: string): string {
    if (action === "review_firm_candidates") return "候補確認";
    if (action === "review_submission") return "内容確認";
    if (action === "confirm_or_discard") return "紐付け/破棄";
    if (action === "review_field_update_proposal") return "更新案確認";
    if (action === "restore_if_needed") return "復元確認";
    return "対応なし";
  }

  function formLabel(formId: string): string {
    return formOptions.find((form) => form.id === formId)?.name ?? formId;
  }

  function jsonText(value: unknown): string {
    return JSON.stringify(value ?? null, null, 2);
  }

  function proposalStatusLabel(status: FieldUpdateProposalStatus): string {
    if (status === "pending") return "確認待ち";
    if (status === "applied") return "適用済み";
    return "却下済み";
  }

  function proposalStatusTagClass(status: FieldUpdateProposalStatus): string {
    if (status === "pending") return "tag tag-warn";
    if (status === "applied") return "tag tag-ok";
    return "tag tag-danger";
  }

  function proposalEntityLabel(
    entity: FieldUpdateProposalChange["entity"],
  ): string {
    return entity === "firm" ? "事務所" : "弁護士";
  }

  function proposalChangeKindLabel(
    kind: FieldUpdateProposalChange["change_kind"],
  ): string {
    return kind === "blank_completion" ? "空欄補完" : "既存値変更";
  }

  function proposalDecisionLabel(
    decision: FieldUpdateProposalDecision | null,
  ): string {
    if (decision === "pending") return "承認待ち";
    if (decision === "approved") return "承認済み";
    if (decision === "rejected") return "却下済み";
    if (decision === "auto_approved") return "自動承認";
    return "未対応";
  }

  function proposalDecisionTagClass(
    decision: FieldUpdateProposalDecision | null,
  ): string {
    if (decision === "pending") return "tag tag-warn";
    if (decision === "approved" || decision === "auto_approved")
      return "tag tag-ok";
    if (decision === "rejected") return "tag tag-danger";
    return "tag";
  }

  function proposalValueText(value: unknown): string {
    if (value === null || value === undefined) return "未設定";
    if (typeof value === "string") return value.length === 0 ? "空文字" : value;
    if (typeof value === "number" || typeof value === "boolean")
      return String(value);
    if (Array.isArray(value)) {
      return value.length === 0 ? "空配列" : value.map(String).join("、");
    }
    return JSON.stringify(value);
  }

  function canGenerateProposal(submission: FormSubmission | null): boolean {
    if (!submission) return false;
    if (
      submission.matching_status === "spam" ||
      submission.matching_status === "discarded"
    ) {
      return false;
    }
    return Boolean(submission.matched_firm_id && submission.matched_lawyer_id);
  }

  function generateProposalTitle(): string {
    if (!allowEdit) return "権限がありません";
    if (!canGenerateFieldUpdateProposal) {
      return "matched_firm_id と matched_lawyer_id が必要です";
    }
    return "";
  }

  function applyProposalTitle(): string {
    if (!allowEdit) return "権限がありません";
    if (!fieldUpdateProposal) return "更新案がありません";
    if (!fieldUpdateProposalState.isPending) return "確認待ちではありません";
    if (fieldUpdateProposalState.hasConflicts)
      return "競合があるため適用できません";
    if (fieldUpdateProposalState.pendingDecisionCount > 0)
      return "承認待ちの項目があります";
    return "";
  }

  function rejectProposalTitle(): string {
    if (!allowEdit) return "権限がありません";
    if (!fieldUpdateProposal) return "更新案がありません";
    if (!fieldUpdateProposalState.isPending) return "確認待ちではありません";
    return "";
  }

  function emptyToNull(value: string): string | null {
    const trimmed = value.trim();
    return trimmed.length === 0 ? null : trimmed;
  }

  function load(): void {
    actionError = null;
    actionMessage = null;
    formSubmissionDetailStore.clear();
    void formListStore.refresh();
    void formSubmissionDetailStore.load(id);
  }

  function backToList(ev: MouseEvent): void {
    if (ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey || ev.button !== 0)
      return;
    ev.preventDefault();
    navigate("/form-submissions");
  }

  function firmPath(firmId: string): string {
    return `/firms/${firmId}`;
  }

  function handleLinkClick(ev: MouseEvent, path: string): void {
    if (ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey || ev.button !== 0)
      return;
    ev.preventDefault();
    navigate(path);
  }

  async function runMutation(
    mutation: () => Promise<unknown>,
    message: string,
  ): Promise<void> {
    if (!allowEdit || actionBusy) return;
    actionBusy = true;
    actionError = null;
    actionMessage = null;
    try {
      await mutation();
      actionMessage = message;
    } catch (err) {
      actionError = err instanceof Error ? err.message : String(err);
    } finally {
      actionBusy = false;
    }
  }

  async function confirmMatch(): Promise<void> {
    const firmId = matchDraft.firm_id.trim();
    if (!firmId) {
      actionError = "firm_id を入力してください。";
      return;
    }
    await runMutation(
      () =>
        confirmFormSubmissionMatch(id, {
          firm_id: firmId,
          lawyer_id: emptyToNull(matchDraft.lawyer_id),
        }),
      "紐付けを確定しました。",
    );
  }

  async function confirmCandidate(
    candidate: FormSubmissionFirmCandidate,
  ): Promise<void> {
    matchDraft = { firm_id: candidate.firm_id, lawyer_id: "" };
    await confirmMatch();
  }

  async function generateFieldUpdateProposal(): Promise<void> {
    if (
      fieldUpdateProposal?.schema_version === 2 &&
      typeof window !== "undefined" &&
      !window.confirm("CRM更新案を再生成します。項目ごとの判断はリセットされます。")
    ) {
      return;
    }
    await runMutation(
      () => generateFormSubmissionFieldUpdateProposal(id),
      fieldUpdateProposal
        ? "CRM更新案を再生成しました。"
        : "CRM更新案を生成しました。",
    );
  }

  async function applyFieldUpdateProposal(): Promise<void> {
    await runMutation(
      () => applyFormSubmissionFieldUpdateProposal(id),
      "CRM更新案を適用しました。",
    );
  }

  async function rejectFieldUpdateProposal(): Promise<void> {
    await runMutation(
      () => rejectFormSubmissionFieldUpdateProposal(id),
      "CRM更新案を却下しました。",
    );
  }

  function canApproveProposalChange(change: FieldUpdateProposalChange): boolean {
    return (
      fieldUpdateProposalState.canUseItemActions &&
      change.decision === "pending" &&
      change.applied_at === null &&
      change.change_id !== null
    );
  }

  function canRejectProposalChange(change: FieldUpdateProposalChange): boolean {
    return (
      fieldUpdateProposalState.canUseItemActions &&
      change.decision === "pending" &&
      change.applied_at === null &&
      change.change_id !== null
    );
  }

  function approveProposalChangeTitle(change: FieldUpdateProposalChange): string {
    if (!allowEdit) return "権限がありません";
    if (!fieldUpdateProposalState.canUseItemActions) return "項目操作できません";
    if (change.change_id === null) return "change_id がありません";
    if (change.applied_at !== null) return "適用済みです";
    if (change.decision !== "pending") return "承認待ちではありません";
    return "";
  }

  function rejectProposalChangeTitle(change: FieldUpdateProposalChange): string {
    if (!allowEdit) return "権限がありません";
    if (!fieldUpdateProposalState.canUseItemActions) return "項目操作できません";
    if (change.change_id === null) return "change_id がありません";
    if (change.applied_at !== null) return "適用済みです";
    if (change.decision !== "pending") return "承認待ちではありません";
    return "";
  }

  async function approveFieldUpdateProposalChange(
    change: FieldUpdateProposalChange,
  ): Promise<void> {
    if (!change.change_id) return;
    await runMutation(
      () => approveFormSubmissionFieldUpdateProposalChange(id, change.change_id!),
      "CRM更新案の項目を承認しました。",
    );
  }

  async function rejectFieldUpdateProposalChange(
    change: FieldUpdateProposalChange,
  ): Promise<void> {
    if (!change.change_id) return;
    await runMutation(
      () => rejectFormSubmissionFieldUpdateProposalChange(id, change.change_id!),
      "CRM更新案の項目を却下しました。",
    );
  }

  onMount(load);

  onDestroy(() => {
    formSubmissionDetailStore.clear();
  });
</script>

<div class="back-link">
  <a href="/form-submissions" onclick={backToList}>フォーム受信トレイへ戻る</a>
</div>

{#if formSubmissionDetailStore.loading && formSubmissionDetailStore.data === null}
  <section class="panel"><p class="muted">読み込み中...</p></section>
{:else if formSubmissionDetailStore.error}
  <section class="panel">
    <p class="error" role="alert">{formSubmissionDetailStore.error}</p>
  </section>
{:else if formSubmissionDetailStore.data}
  {@const submission = formSubmissionDetailStore.data}
  <header class="detail-head">
    <div>
      <div class="title-row">
        <h1>{formatDateTime(submission.received_at)}</h1>
        <span class={statusTagClass(submission.matching_status)}>
          {statusLabel(submission.matching_status)}
        </span>
      </div>
      <p class="meta">
        <span>{formLabel(submission.form_id)}</span>
        <span>{applicantLabel(submission.applicant_type)}</span>
        <span>{actionLabel(submission.reviewer_next_action)}</span>
      </p>
    </div>
  </header>

  {#if actionError}
    <p class="error" role="alert">{actionError}</p>
  {/if}
  {#if actionMessage}
    <p class="notice" role="status">{actionMessage}</p>
  {/if}

  <section class="panel">
    <div class="panel-header">
      <h2>レビュー操作</h2>
    </div>
    <div class="match-grid">
      <label>
        <span>firm_id</span>
        <input
          bind:value={matchDraft.firm_id}
          disabled={!allowEdit || actionBusy}
        />
      </label>
      <label>
        <span>lawyer_id</span>
        <input
          bind:value={matchDraft.lawyer_id}
          disabled={!allowEdit || actionBusy}
        />
      </label>
      <button
        type="button"
        class="btn btn-primary"
        disabled={!allowEdit || actionBusy}
        title={allowEdit ? "" : "権限がありません"}
        onclick={() => void confirmMatch()}
      >
        紐付け確定
      </button>
    </div>
    <div class="action-row">
      <button
        type="button"
        class="btn"
        disabled={!allowEdit || actionBusy || !submission.matched_firm_id}
        title={allowEdit ? "" : "権限がありません"}
        onclick={() =>
          void runMutation(
            () => clearFormSubmissionMatch(id),
            "紐付けを解除しました。",
          )}
      >
        紐付け解除
      </button>
      {#if submission.matching_status === "spam" || submission.matching_status === "discarded"}
        <button
          type="button"
          class="btn btn-primary"
          disabled={!allowEdit || actionBusy}
          title={allowEdit ? "" : "権限がありません"}
          onclick={() =>
            void runMutation(() => restoreFormSubmission(id), "復元しました。")}
        >
          復元
        </button>
      {:else}
        <button
          type="button"
          class="btn"
          disabled={!allowEdit || actionBusy}
          title={allowEdit ? "" : "権限がありません"}
          onclick={() =>
            void runMutation(
              () => discardFormSubmission(id),
              "送信を破棄しました。",
            )}
        >
          破棄
        </button>
        <button
          type="button"
          class="btn btn-danger"
          disabled={!allowEdit || actionBusy}
          title={allowEdit ? "" : "権限がありません"}
          onclick={() =>
            void runMutation(
              () => markFormSubmissionSpam(id),
              "スパムにしました。",
            )}
        >
          スパム
        </button>
      {/if}
    </div>
  </section>

  <section class="panel">
    <div class="panel-header proposal-header">
      <div class="proposal-title">
        <h2>CRM更新案</h2>
        {#if fieldUpdateProposal}
          <div class="row-sub">
            生成 {formatDateTime(fieldUpdateProposal.generated_at)}
            {#if fieldUpdateProposal.reviewed_at}
              / 確認 {formatDateTime(fieldUpdateProposal.reviewed_at)}
            {/if}
          </div>
        {/if}
      </div>
      <div class="proposal-actions">
        {#if fieldUpdateProposal}
          <span class={proposalStatusTagClass(fieldUpdateProposal.status)}>
            {proposalStatusLabel(fieldUpdateProposal.status)}
          </span>
        {/if}
        <button
          type="button"
          class="btn"
          disabled={!allowEdit || actionBusy || !canGenerateFieldUpdateProposal}
          title={generateProposalTitle()}
          onclick={() => void generateFieldUpdateProposal()}
        >
          {fieldUpdateProposal ? "再生成" : "生成"}
        </button>
        {#if fieldUpdateProposal}
          <button
            type="button"
            class="btn btn-primary"
            disabled={!allowEdit ||
              actionBusy ||
              !fieldUpdateProposalState.canApply}
            title={applyProposalTitle()}
            onclick={() => void applyFieldUpdateProposal()}
          >
            適用
          </button>
          {#if fieldUpdateProposal.schema_version !== 2 ||
            fieldUpdateProposalState.hasConflicts ||
            fieldUpdateProposal.changes.length === 0}
            <button
              type="button"
              class="btn"
              disabled={!allowEdit ||
                actionBusy ||
                !fieldUpdateProposalState.canReject}
              title={rejectProposalTitle()}
              onclick={() => void rejectFieldUpdateProposal()}
            >
              却下
            </button>
          {/if}
        {/if}
      </div>
    </div>

    {#if fieldUpdateProposal}
      <dl class="proposal-summary">
        <div>
          <dt>変更</dt>
          <dd>{fieldUpdateProposalState.changeCount}件</dd>
        </div>
        <div>
          <dt>承認待ち</dt>
          <dd>{fieldUpdateProposalState.pendingDecisionCount}件</dd>
        </div>
        <div>
          <dt>承認済み</dt>
          <dd>{fieldUpdateProposalState.approvedCount}件</dd>
        </div>
        <div>
          <dt>自動承認</dt>
          <dd>{fieldUpdateProposalState.autoApprovedCount}件</dd>
        </div>
        <div>
          <dt>却下済み</dt>
          <dd>{fieldUpdateProposalState.rejectedCount}件</dd>
        </div>
        <div>
          <dt>適用済み</dt>
          <dd>{fieldUpdateProposalState.appliedCount}件</dd>
        </div>
        <div>
          <dt>競合</dt>
          <dd>
            {fieldUpdateProposalState.hasConflicts
              ? `${fieldUpdateProposal.conflicted.length}件`
              : "なし"}
          </dd>
        </div>
      </dl>

      {#if fieldUpdateProposal.conflicted.length > 0}
        <div class="proposal-warning" role="status">
          {#each fieldUpdateProposal.conflicted as conflict (`${conflict.entity}:${conflict.field}`)}
            <span
              >{proposalEntityLabel(conflict.entity)} / {conflict.field}</span
            >
          {/each}
        </div>
      {/if}

      {#if fieldUpdateProposal.changes.length === 0}
        <p class="empty">CRM更新差分はありません。</p>
      {:else if fieldUpdateProposal.schema_version === 2}
        <div class="table-scroll">
          <table class="proposal-table proposal-table-v2">
            <thead>
              <tr>
                <th>対象</th>
                <th>項目</th>
                <th>現在</th>
                <th>送信値</th>
                <th>種別</th>
                <th>判断</th>
                <th>適用</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {#each fieldUpdateProposal.changes as change, index (`${change.change_id ?? `${change.entity}:${change.field}`}:${index}`)}
                <tr>
                  <td>{proposalEntityLabel(change.entity)}</td>
                  <td><code>{change.field}</code></td>
                  <td class="proposal-value">{proposalValueText(change.before)}</td>
                  <td class="proposal-value">{proposalValueText(change.after)}</td>
                  <td>{proposalChangeKindLabel(change.change_kind)}</td>
                  <td>
                    <span class={proposalDecisionTagClass(change.decision)}>
                      {proposalDecisionLabel(change.decision)}
                    </span>
                    {#if change.decided_at}
                      <div class="row-sub">{formatDateTime(change.decided_at)}</div>
                    {/if}
                  </td>
                  <td>
                    {#if change.applied_at}
                      <span class="tag tag-ok">適用済み</span>
                      <div class="row-sub">{formatDateTime(change.applied_at)}</div>
                    {:else}
                      <span class="tag">未適用</span>
                    {/if}
                  </td>
                  <td>
                    <div class="proposal-row-actions">
                      <button
                        type="button"
                        class="btn tiny"
                        disabled={!allowEdit ||
                          actionBusy ||
                          !canApproveProposalChange(change)}
                        title={approveProposalChangeTitle(change)}
                        onclick={() => void approveFieldUpdateProposalChange(change)}
                      >
                        承認
                      </button>
                      <button
                        type="button"
                        class="btn tiny"
                        disabled={!allowEdit ||
                          actionBusy ||
                          !canRejectProposalChange(change)}
                        title={rejectProposalChangeTitle(change)}
                        onclick={() => void rejectFieldUpdateProposalChange(change)}
                      >
                        却下
                      </button>
                    </div>
                  </td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      {:else}
        <div class="table-scroll">
          <table class="proposal-table">
            <thead>
              <tr>
                <th>対象</th>
                <th>項目</th>
                <th>現在</th>
                <th>送信値</th>
                <th>種別</th>
                <th>承認</th>
              </tr>
            </thead>
            <tbody>
              {#each fieldUpdateProposal.changes as change, index (`${change.entity}:${change.field}:${index}`)}
                <tr>
                  <td>{proposalEntityLabel(change.entity)}</td>
                  <td><code>{change.field}</code></td>
                  <td class="proposal-value"
                    >{proposalValueText(change.before)}</td
                  >
                  <td class="proposal-value"
                    >{proposalValueText(change.after)}</td
                  >
                  <td>{proposalChangeKindLabel(change.change_kind)}</td>
                  <td>
                    <span
                      class={change.requires_approval
                        ? "tag tag-warn"
                        : "tag tag-ok"}
                    >
                      {change.requires_approval ? "必要" : "不要"}
                    </span>
                  </td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      {/if}

      <details class="raw-details">
        <summary>JSON</summary>
        <pre>{jsonText(submission.field_update_proposal)}</pre>
      </details>
    {:else if submission.field_update_proposal}
      <p class="error" role="alert">CRM更新案の形式を表示できません。</p>
      <pre>{jsonText(submission.field_update_proposal)}</pre>
    {:else}
      <p class="empty">CRM更新案はまだありません。</p>
    {/if}
  </section>

  <section class="panel">
    <div class="panel-header">
      <h2>候補</h2>
      <span class="muted">{submission.firm_candidate_count}件</span>
    </div>
    {#if submission.firm_candidate_summaries.length === 0}
      <p class="empty">候補はありません。</p>
    {:else}
      <div class="candidate-list">
        {#each submission.firm_candidate_summaries as candidate (candidate.firm_id)}
          <article class="candidate-row">
            <div class="candidate-main">
              <a
                href={firmPath(candidate.firm_id)}
                onclick={(ev) =>
                  handleLinkClick(ev, firmPath(candidate.firm_id))}
              >
                {candidate.official_name}
              </a>
              <div class="row-sub">
                {[candidate.prefecture, candidate.city]
                  .filter(Boolean)
                  .join(" / ") || "-"}
              </div>
              <div class="reason-list">
                {#each candidate.reasons as reason (reason)}
                  <span class="tag">{reason}</span>
                {/each}
              </div>
            </div>
            <pre>{jsonText(candidate.matched_values)}</pre>
            <button
              type="button"
              class="btn"
              disabled={!allowEdit || actionBusy}
              title={allowEdit ? "" : "権限がありません"}
              onclick={() => void confirmCandidate(candidate)}
            >
              候補で確定
            </button>
          </article>
        {/each}
      </div>
    {/if}
  </section>

  <section class="panel">
    <div class="panel-header">
      <h2>送信内容</h2>
    </div>
    <dl class="summary-list">
      <div>
        <dt>submission_id</dt>
        <dd><code>{submission.id}</code></dd>
      </div>
      <div>
        <dt>matched_firm_id</dt>
        <dd>
          {submission.matched_firm_id ? submission.matched_firm_id : "未設定"}
        </dd>
      </div>
      <div>
        <dt>matched_lawyer_id</dt>
        <dd>
          {submission.matched_lawyer_id
            ? submission.matched_lawyer_id
            : "未設定"}
        </dd>
      </div>
      <div>
        <dt>dialogue_log_id</dt>
        <dd>
          {submission.dialogue_log_id
            ? submission.dialogue_log_id
            : "未設定"}
        </dd>
      </div>
      <div>
        <dt>contact_logged</dt>
        <dd>{submission.contact_logged ? "yes" : "no"}</dd>
      </div>
      <div>
        <dt>field_update_applied</dt>
        <dd>{submission.field_update_applied ? "yes" : "no"}</dd>
      </div>
    </dl>
    <div class="json-grid">
      <section>
        <h3>raw_payload</h3>
        <pre>{jsonText(submission.raw_payload)}</pre>
      </section>
      <section>
        <h3>source_context</h3>
        <pre>{jsonText(submission.source_context)}</pre>
      </section>
      {#if submission.firm_candidates}
        <section>
          <h3>firm_candidates</h3>
          <pre>{jsonText(submission.firm_candidates)}</pre>
        </section>
      {/if}
    </div>
  </section>
{/if}

<style>
  .back-link {
    margin-bottom: 10px;
  }
  .back-link a {
    font-size: 13px;
  }
  .detail-head {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    align-items: flex-start;
    margin-bottom: 12px;
  }
  .title-row {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
  }
  h1 {
    margin: 0;
    font-size: 1.35rem;
  }
  .meta {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
    margin: 4px 0 0;
    color: var(--muted);
    font-size: 12px;
  }
  .panel-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 12px;
  }
  .panel-header h2 {
    margin: 0;
    font-size: 1rem;
  }
  .notice {
    color: #065f46;
    background: #ecfdf5;
    border: 1px solid #a7f3d0;
    border-radius: var(--radius);
    padding: 10px 12px;
    margin: 0 0 12px;
  }
  .match-grid {
    display: grid;
    grid-template-columns: minmax(220px, 1fr) minmax(220px, 1fr) auto;
    gap: 10px;
    align-items: end;
  }
  .match-grid label {
    display: grid;
    gap: 4px;
  }
  .match-grid span,
  .summary-list dt {
    color: var(--muted);
    font-size: 12px;
    font-weight: 700;
  }
  .action-row {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    margin-top: 12px;
  }
  .proposal-header {
    align-items: flex-start;
  }
  .proposal-title {
    display: grid;
    gap: 2px;
  }
  .proposal-actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    flex-wrap: wrap;
  }
  .proposal-summary {
    display: grid;
    grid-template-columns: repeat(4, minmax(120px, 1fr));
    gap: 10px 16px;
    margin: 0 0 12px;
  }
  .proposal-summary div {
    display: grid;
    gap: 2px;
  }
  .proposal-summary dt {
    color: var(--muted);
    font-size: 12px;
    font-weight: 700;
  }
  .proposal-summary dd {
    margin: 0;
  }
  .proposal-warning {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
    margin-bottom: 12px;
    padding: 10px 12px;
    border: 1px solid #fcd34d;
    border-radius: var(--radius);
    background: #fffbeb;
    color: #92400e;
    font-size: 12px;
  }
  .proposal-warning span {
    font-weight: 700;
  }
  .proposal-table th,
  .proposal-table td {
    min-width: 90px;
  }
  .proposal-table-v2 th,
  .proposal-table-v2 td {
    vertical-align: top;
  }
  .proposal-value {
    max-width: 260px;
    overflow-wrap: anywhere;
  }
  .proposal-row-actions {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
  }
  .tiny {
    padding: 4px 8px;
    font-size: 12px;
  }
  .raw-details {
    margin-top: 12px;
  }
  .raw-details summary {
    cursor: pointer;
    color: var(--muted);
    font-size: 12px;
    font-weight: 700;
  }
  .raw-details pre {
    margin-top: 8px;
  }
  .candidate-list {
    display: grid;
    gap: 10px;
  }
  .candidate-row {
    display: grid;
    grid-template-columns: minmax(220px, 1fr) minmax(260px, 1fr) auto;
    gap: 12px;
    align-items: start;
    border-top: 1px solid var(--border);
    padding-top: 10px;
  }
  .candidate-row:first-child {
    border-top: 0;
    padding-top: 0;
  }
  .candidate-main a {
    font-weight: 700;
  }
  .row-sub {
    margin-top: 2px;
    color: var(--muted);
    font-size: 11px;
    overflow-wrap: anywhere;
  }
  .reason-list {
    display: flex;
    gap: 4px;
    flex-wrap: wrap;
    margin-top: 6px;
  }
  .summary-list {
    display: grid;
    grid-template-columns: repeat(3, minmax(180px, 1fr));
    gap: 10px 16px;
    margin: 0 0 16px;
  }
  .summary-list div {
    display: grid;
    gap: 2px;
  }
  .summary-list dd {
    margin: 0;
    overflow-wrap: anywhere;
  }
  .json-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
  }
  .json-grid h3 {
    margin: 0 0 6px;
    font-size: 13px;
  }
  pre {
    max-height: 420px;
    overflow: auto;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
    margin: 0;
    padding: 10px;
    border: 1px solid var(--border);
    border-radius: var(--radius);
    background: #f9fafb;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 12px;
  }
  code {
    overflow-wrap: anywhere;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 12px;
  }
  button:disabled,
  input:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
  @media (max-width: 920px) {
    .match-grid,
    .candidate-row,
    .proposal-summary,
    .summary-list,
    .json-grid {
      grid-template-columns: 1fr;
    }
    .proposal-actions {
      justify-content: flex-start;
    }
  }
</style>
